import {
  PACKAGE_WEIGHT_UNITS,
  TableDataObjectType,
  TABLENAMES,
  TableObject,
} from "./baseTableTypes";
import * as yup from "yup";

export function YupTypedSelectOption_v2(
  type: string,
  valueSchema: yup.AnyObjectSchema,
) {
  return yup.object({
    label: yup.string().defined().required(),
    type: yup.string().equals([type]).defined(),
    group: yup.string().optional(),
    value: valueSchema.defined().required(),
  });
}

export function YupWorldLocation(type: "city" | "state" | "country") {
  return yup.object({
    type: yup.string().defined().equals([type]).required(),
    parent: yup.number().defined().required(),
    name: yup.string().defined().required(),
    id: yup.number().defined().required(),
    code: yup.string().defined().required(),
  });
}

export function YupTableObject(
  type: "linked_record",
  params?: Record<string, any>,
): yup.ObjectSchema<TableObject<"linked_record">>;
export function YupTableObject(
  type: "address",
  params?: Record<string, any>,
): yup.ObjectSchema<TableObject<"address">>;
export function YupTableObject(
  type: "orderaddress",
  params?: Record<string, any>,
): yup.ObjectSchema<TableObject<"orderaddress">>;
export function YupTableObject(
  type: "contact",
  params?: Record<string, any>,
): yup.ObjectSchema<TableObject<"contact">>;
export function YupTableObject(
  type: "pkg_dims",
  params?: Record<string, any>,
): yup.ObjectSchema<TableObject<"pkg_dims">>;
export function YupTableObject(
  type: "pkg_weight",
  params?: Record<string, any>,
): yup.ObjectSchema<TableObject<"pkg_weight">>;

export function YupTableObject(
  type: TableDataObjectType,
  params: Record<string, any> = {},
): yup.AnyObjectSchema {
  switch (type) {
    case "linked_record":
      return yup.object({
        rec_id: yup.string().defined().required(),
        rec_table: yup.string().defined().equals(TABLENAMES).required(),
        rec_name: yup.string().defined().required(),
        parent_id: yup.string().defined().nullable(),
        parent_table: yup.string().defined().equals(TABLENAMES).nullable(),
      });
    case "address":
      return yup.object({
        attn: yup
          .string()
          .defined()
          .nullable()
          .test({
            name: "addr-attn",
            test: function (value, context) {
              if (!value && !context.parent.addressee) {
                return context.createError({
                  message: "Either Attn or Addressee must be provided.",
                });
              }
              return true;
            },
          }),
        addressee: yup
          .string()
          .defined()
          .nullable()
          .test({
            name: "addr-addressee",
            test: function (value, context) {
              if (!value && !context.parent.attn) {
                return context.createError({
                  message: "Either Addressee or Attn must be provided.",
                });
              }
              return true;
            },
          }),
        line_1: yup.string().defined().required("Line 1 is required."),
        line_2: yup.string().defined().nullable(),
        line_3: yup.string().defined().nullable(),
        city: yup.string().defined().required("City is required."),
        state: yup.string().defined().required("State is required."),
        country: yup.string().defined().required("Country is required."),
        zip: yup.string().defined().required("Zip/Postal Code is required."),
      });
    case "orderaddress":
      return yup.object({
        attn: yup
          .string()
          .defined()
          .nullable()
          .test({
            name: "addr-attn",
            test: function (value, context) {
              if (!value && !context.parent.addressee) {
                return context.createError({
                  message: "Either Attn or Addressee must be provided.",
                });
              }
              return true;
            },
          }),
        addressee: yup
          .string()
          .defined()
          .nullable()
          .test({
            name: "addr-addressee",
            test: function (value, context) {
              if (!value && !context.parent.attn) {
                return context.createError({
                  message: "Either Addressee or Attn must be provided.",
                });
              }
              return true;
            },
          }),
        line_1: yup.string().defined().required("Line 1 is required."),
        line_2: yup.string().defined().nullable(),
        line_3: yup.string().defined().nullable(),
        city: yup.string().defined().required("City is required."),
        state: yup.string().defined().required("State is required."),
        country: yup.string().defined().required("Country is required."),
        zip: yup.string().defined().required("Zip/Postal Code is required."),
        phone: yup.string().defined().nullable(),
        email: yup.string().defined().nullable(),
      });
    case "contact":
      return yup.object({
        name: yup.string().defined().required("Contact Name is required."),
        email: !!params["email"]
          ? yup.string().defined().required("Contact Email is required.")
          : yup.string().defined().nullable(),
        phone: yup.string().defined().nullable(),
      });
    case "pkg_dims":
      return yup.object({
        height: yup
          .number()
          .defined()
          // eslint-disable-next-line no-template-curly-in-string
          .min(0.1, "Height must be at least ${min}")
          .required("Height is required."),
        width: yup
          .number()
          .defined()
          // eslint-disable-next-line no-template-curly-in-string
          .min(0.1, "Width must be at least ${min}")
          .required("Width is required."),
        length: yup
          .number()
          .defined()
          // eslint-disable-next-line no-template-curly-in-string
          .min(0.1, "Length must be at least ${min}")
          .required("Length is required."),
      });
    case "pkg_weight":
      return yup.object({
        value: yup
          .number()
          .defined()
          // eslint-disable-next-line no-template-curly-in-string
          .min(0.1, "Weight must be at least ${min}")
          .required("Weight value is required."),
        unit: yup
          .string()
          .defined()
          .equals(PACKAGE_WEIGHT_UNITS)
          .required("Weight unit is required."),
      });
  }
}

export const YupBaseCreateSchema = {
  description: yup.string().nullable(),
  is_record_active: yup.boolean().nullable(),
};
